import { type JSX } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import {
  RentComplexProps,
  RentComplex,
} from '@features/effector-form/controls/rent-complex-controls/types';
import { withControl } from '@features/effector-form/hoc/withControl';
import { RentComplexesControlGate, rentComplexesQuery } from '../model';

export const RentComplexControl = withControl<RentComplexProps>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    helperText,
    city = null,
    onCustomChange,
    name,
    conditionForChange,
    isError,
    excludeOptions = [],
  }) => {
    useGate(RentComplexesControlGate, { cities: city as number });

    const { data, pending } = useUnit(rentComplexesQuery);

    const val = value !== '' ? data?.find((item) => item.id === value) : null;

    const handleChange = (_: unknown, newValue: any) =>
      onCustomChange && conditionForChange
        ? onCustomChange({ value: newValue?.id || null, path: name })
        : setValue(newValue?.id || null);

    const handleFilterOptions = (
      opts: RentComplex[],
      { inputValue }: { inputValue: string }
    ) => {
      const currentOptions = excludeOptions.length
        ? opts.reduce(
            (acc: RentComplex[], option) =>
              !excludeOptions.includes(option.id) ? [...acc, option] : acc,
            []
          )
        : opts;

      if (inputValue) {
        return currentOptions.filter((opt) =>
          opt?.label?.toUpperCase().includes(inputValue.toUpperCase())
        );
      }

      return currentOptions;
    };

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          filterOptions={handleFilterOptions}
          loading={pending}
          value={!data ? null : val || null}
          options={data || []}
          onChange={handleChange}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  error: isError,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
